import { Box, InputAdornment, OutlinedInput, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import songjukti from "../../images/songjukti_logo.png";
import PriceSummaryRow from "./PriceSummaryRow";
import { SERVICE_FEE_RATE, formatCurrencyAmount, getCurrencyLabel, splitByServiceFee } from "./priceUtils";
import { tokens } from "../../store/tokens";

const inputSx = {
  borderRadius: `${tokens.input.radius}px`,
  bgcolor: tokens.input.bg,
  fontSize: tokens.input.fontSize.sm,
  "& fieldset": { borderColor: tokens.input.border },
  "&:hover fieldset": { borderColor: tokens.input.borderHover },
  "&.Mui-focused fieldset": { borderColor: tokens.input.borderFocus },
};

const ByProjectForm = ({ setCpInfo, cpInfo, fetchJobDetails, handleChange, hourly }) => {
  const currency = fetchJobDetails?.currency;
  const budgetValue = cpInfo?.budget ?? "";

  // Single source of truth: everything is derived from the budget the
  // freelancer typed, so the fee and payout can never drift out of sync
  // with what's on screen.
  const { charge, receiveAmount } = useMemo(
    () => splitByServiceFee(budgetValue),
    [budgetValue]
  );

  // Keep cpInfo up to date for whatever consumes it later (review step,
  // submission payload) without making the input itself uncontrolled.
  useEffect(() => {
    setCpInfo((prev) => ({ ...prev, budgetCharge: charge, clientPrice: receiveAmount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charge, receiveAmount]);

  return (
    <Stack spacing={1.5} alignItems="flex-start" sx={{ width: "100%" }}>
      <Typography
        sx={{
          fontFamily: tokens.font.body,
          fontSize: "1.05rem",
          fontWeight: 700,
          color: tokens.color.ink,
        }}
      >
        What is the rate you'd like to bid for this job?
      </Typography>

      <Box
        sx={{
          width: "100%",
          borderRadius: `${tokens.radius.md}px`,
          border: `1px solid ${tokens.color.line}`,
          overflow: "hidden",
          bgcolor: tokens.bg.surface,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            padding: "16px",
            borderBottom: `1px solid ${tokens.color.line}`,
          }}
        >
          <Stack sx={{ maxWidth: "65%" }}>
            <Typography
              sx={{
                fontFamily: tokens.font.body,
                fontSize: "0.95rem",
                fontWeight: 600,
                color: tokens.color.ink,
              }}
            >
              {hourly ? "Hourly Rate" : "Proposed Amount"}
            </Typography>
            <Typography
              sx={{
                fontFamily: tokens.font.body,
                fontSize: "0.8rem",
                color: tokens.color.inkMuted,
                mt: 0.25,
              }}
            >
              Total amount the client will see on your proposal
            </Typography>
          </Stack>

          <OutlinedInput
            name="budget"
            type="number"
            size="small"
            value={budgetValue}
            placeholder="Enter amount"
            startAdornment={
              <InputAdornment position="start">
                <Typography
                  sx={{
                    fontFamily: tokens.font.body,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: tokens.color.inkMuted,
                  }}
                >
                  {getCurrencyLabel(currency)}
                </Typography>
              </InputAdornment>
            }
            sx={{ width: { xs: "45%", sm: 190 }, ...inputSx }}
            onChange={handleChange}
          />
        </Stack>

        <PriceSummaryRow
          icon={
            <Box
              component="img"
              src={songjukti}
              alt=""
              sx={{ width: 16, height: 16, borderRadius: "50%" }}
            />
          }
          label={`Charge (${Math.round(SERVICE_FEE_RATE * 100)}%)`}
          value={formatCurrencyAmount(charge, currency)}
        />

        <PriceSummaryRow
          label="You'll Receive"
          description="Your estimated payment, after service fees"
          value={formatCurrencyAmount(receiveAmount, currency)}
          highlight
          isLast
        />
      </Box>
    </Stack>
  );
};

export default ByProjectForm;